import { Tooltip } from "antd";
import React, { useMemo } from "react";
import { convertUtcToLocal } from "../../../library/utils";
import { DATE_TIME_FORMATS } from "../../../utils/dateTimes";
import AuditLogIcon from "./auditLog/AuditLogIcon";
import { AUDIT_LOG_EMPLOYEE_ACTOR_TYPES } from "./auditLog/constants";
import mapAuditLogEntry from "./auditLog/mapAuditLogEntry";

/**
 * A consent change in the conversation feed (`messageType: CONSENT_UPDATE`).
 *
 * These are audit events, not conversation, so they render as a centered
 * dashed pill rather than a bubble (EAI-1941, Figma node 27292-9591):
 *
 *   [icon] Auto response ● Enrolled | By Support EngagedAi • 1:40 PM
 *
 * category glyph, subject, a status dot + medium-weight status, a hairline
 * divider, then actor and time — with the full date still on hover.
 *
 * The message's `content` is the same audit record the Audit log drawer shows
 * (it carries the matching `audit_log_id`), just snake_cased, so the wording and
 * the actor rules come from the shared `mapAuditLogEntry` and the two surfaces
 * cannot drift.
 */
const ConsentUpdateMessage = ({ message, userIdMap, leadName }) => {
  const item = useMemo(() => {
    // `content` is a JSON audit record, not prose. If it is not parseable there
    // is nothing to say, and dumping the raw string into the thread would be
    // worse than staying silent.
    try {
      const payload = JSON.parse(message?.content || "");
      if (!payload || typeof payload !== "object") return null;
      return { ...mapAuditLogEntry(payload, null, leadName), updatedById: payload.updated_by_id };
    } catch {
      return null;
    }
  }, [message?.content, leadName]);

  if (!item) return null;

  // `mapAuditLogEntry` has already applied the attribution rules, so a lead's
  // or the system's row is named there and nothing here may override it — the
  // message can carry a `user` even for an automated change. Only an employee
  // row benefits from the thread's extra knowledge: the payload names the actor
  // by id, and the message record plus the thread's id→name map resolve it when
  // the audit row itself carries no name.
  const needsEmployeeLookup =
    AUDIT_LOG_EMPLOYEE_ACTOR_TYPES.includes(item.updatedByType) && !item.updatedByName;
  const employeeName =
    (+message?.user?.id === +item.updatedById ? message?.user?.fullName : null) ||
    userIdMap?.[+item.updatedById];
  const actorName = (needsEmployeeLookup && employeeName) || item.metaPrefix;

  const sentAt = message?.dateReceived || message?.dateSent;
  const time = convertUtcToLocal(sentAt, DATE_TIME_FORMATS.HH_MM_A);
  const fullTimestamp = convertUtcToLocal(sentAt, DATE_TIME_FORMATS.MMM_D_YYYY_DOT_h_mm_A);

  // "By {actor} • {time}", dropping any part that did not resolve rather than
  // leaving a dangling separator.
  const meta = [actorName && `By ${actorName}`, sentAt && time].filter(Boolean).join(" • ");

  return (
    <div className="flex w-full justify-center px-4">
      <Tooltip title={fullTimestamp}>
        <span className="relative inline-flex max-w-full flex-wrap items-center justify-center gap-x-1.5 rounded-[15px] bg-white px-2.5 py-1 text-center text-sm font-normal leading-[22px] text-grey-500">
          {/* The design's dash is 4px on / 5px off. CSS `border-dashed` cannot
              set the dash length (Chrome draws ~3/3 at 1px), so the border is
              an SVG stroke over the pill. */}
          <svg
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
          >
            <rect
              x="0.5"
              y="0.5"
              rx="14.5"
              style={{ width: "calc(100% - 1px)", height: "calc(100% - 1px)" }}
              fill="none"
              stroke="#D9D9D9"
              strokeDasharray="4 5"
            />
          </svg>
          <span className="flex shrink-0 items-center">
            <AuditLogIcon icon={item.icon} color="currentColor" />
          </span>
          <span>{item.pillSubject}</span>
          {item.pillStatus && (
            <span className="inline-flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="h-1.5 w-1.5 shrink-0 rounded-full"
                style={{ backgroundColor: item.pillDotColor }}
              />
              <span className="font-medium text-grey-700">{item.pillStatus}</span>
            </span>
          )}
          {meta && (
            <>
              <span aria-hidden="true" className="h-3.5 w-px shrink-0 bg-[#D9D9D9]" />
              <span>{meta}</span>
            </>
          )}
        </span>
      </Tooltip>
    </div>
  );
};

export default ConsentUpdateMessage;
